// Config
import appConfig from '../../config/appConfig';

// React Hooks
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from 'react-router-dom';

// Import esterni
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMobileAlt, faBars, faChevronDown, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faFacebookF, faInstagram, faTwitter, faLinkedin, faPinterest, faYoutube } from '@fortawesome/free-brands-svg-icons';

export interface MenuItem {
    label: string;
    url: string;
    title: string;
    dropdown?: boolean;
    items?: MenuItem[];
}

export interface AgencyConfig {
    agency: {
        customPhone: string;
        customMobile: string;
    };
    socialLinks: {
        whatsapp: string;
        facebook: string | boolean;
        instagram: string | boolean;
        youtube?: string | boolean;
        linkedin: string | boolean;
    };
}

export interface AgencyData {
    agency_name: string;
    address: string;
    phone1: string;
    mobile: string;
    mobile2: string;
    international_prefix: string;
    whatsapp: string | boolean;
    youtube?: string | boolean;
    facebook: string | boolean;
    instagram: string | boolean;
    twitter: string | boolean;
    linkedin: string | boolean;
    pinterest: string | boolean;
    cookie_solution_iubenda: boolean;
}

interface HeaderProps {
    agencyData: AgencyData;
    languageSelector: React.ReactElement;
    assets?: {
        logoPath?: string;
        menuItemsPath?: string;
    };
    className?: string;
    onMenuItemClick?: (item: MenuItem) => void;
}

const Header: React.FC<HeaderProps> = ({
    agencyData,
    languageSelector,
    assets = {
        logoPath: "/assets/images/logo.png",
        menuItemsPath: "/locales"
    },
    className = "",
    onMenuItemClick,
}) => {

    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [menuLoading, setMenuLoading] = useState(true);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const [collapsed, setCollapsed] = useState(false);
    const [dropDownCollapsed, setDropDownCollapsed] = useState(false);

    useEffect(() => {
        const loadMenuItems = async () => {
            const langCode = currentLanguage.split('-')[0];

            if (!langCode || langCode.includes('cimode')) {
                setMenuItems([]);
                setMenuLoading(false);
                return;
            }

            setMenuLoading(true);
            try {
                const response = await fetch(`${assets.menuItemsPath}/${langCode}/menuItems.json`);

                console.log(response);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setMenuItems(data || []);

            } catch (error) {
                console.error(`Failed to load menu items for language: ${langCode}`, error);
                setMenuItems([]);
            } finally {
                setMenuLoading(false);
            }
        };

        if (currentLanguage) {
            loadMenuItems();
        }

    }, [currentLanguage, assets.menuItemsPath]);

    const handleMenuItemClick = (item: MenuItem) => {
        setDropDownCollapsed(false);
        setCollapsed(false);
        onMenuItemClick?.(item);
    };

    const location = useLocation();
    const [isAbsolute, setIsAbsolute] = useState(true);
    
    useEffect(() => {
        if (location.pathname === '/') {
            setIsAbsolute(true);
        } else {
            setIsAbsolute(false);
        }
    }, [location]);



    return (
        <header className={`header ${className} ${isAbsolute ? 'position-absolute' : 'position-relative'}`}>
            <div className="container-fluid px-0">
                <div className="row mx-0">
                    <div className="col-auto px-0">
                        {/* Logo Navbar */}
                        <div className='bg-white'>
                            <Link className="navbar-brand pl-3" to={`/`} title={t('navigation.homeLinkTitle') as string}>
                                <img
                                    className="img-fluid"
                                    src={assets.logoPath}
                                    alt={`${t('header.navbar.logoAlt')}`}
                                />
                            </Link>
                        </div>
                    </div>
                    <div className="col position-static px-0">
                        <div className="d-flex flex-column justify-content-evenly h-100">
                            <div className="topbar d-none d-md-block px-3">
                                <div className="row justify-content-between">
                                    <div className="col col-lg d-flex flex-column flex-sm-row">
                                        <div className="d-inline-block mr-sm-4 mb-1 mb-md-0">
                                            <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
                                            <span>{appConfig.agency.customAddress !== "" ? appConfig.agency.customAddress : agencyData.address}</span>
                                        </div>
                                        {(appConfig.agency.customMobile !== "") || (agencyData.mobile && agencyData.mobile !== "N/A") && (
                                            <div className="d-inline-block mr-sm-1">
                                                {appConfig.agency.customMobile !== "" ? (
                                                    <a href={`tel:${agencyData.international_prefix + appConfig.agency.customMobile}`} title={`${t('header.topbar.mobileTitle')}`}>
                                                        <FontAwesomeIcon icon={faMobileAlt} className="mr-2" style={{ width: "14px" }} />
                                                        <span className="phoneNumber">{agencyData.international_prefix + " " + appConfig.agency.customMobile}</span>
                                                    </a>
                                                ) : (
                                                    <a href={`tel:${agencyData.international_prefix || ''}${agencyData.mobile}`} title={`${t('header.topbar.mobileTitle')}`}>
                                                        <FontAwesomeIcon icon={faMobileAlt} className="mr-2" style={{ width: "14px" }} />
                                                        <span className="phoneNumber">{`${agencyData.international_prefix || ''} ${agencyData.mobile}`}</span>
                                                    </a>
                                                )}
                                            </div>
                                        )}

                                        <div className="d-inline-block">
                                            {agencyData.mobile2 && agencyData.mobile2 !== "N/A" && (
                                                <a href={`tel:${agencyData.international_prefix || ''}${agencyData.mobile2}`} title={`${t('header.topbar.mobileTitle')}`}>
                                                    <FontAwesomeIcon icon={faMobileAlt} className="mr-2" style={{ width: "14px" }} />
                                                    <span className="phoneNumber">{`${agencyData.international_prefix || ''} ${agencyData.mobile2}`}</span>
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-auto d-flex flex-column flex-sm-row justify-content-sm-end">
                                        <div className="d-flex align-items-center">
                                            {/* Social Icons */}
                                            <div className="social">
                                                <ul className="list-unstyled d-flex">
                                                    {agencyData.whatsapp && (
                                                        <li className="mr-2">
                                                            <a
                                                                title={`${t('footer.social.whatsappTitle')}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                href={'https://web.whatsapp.com/send?phone=' + appConfig.socialLinks.whatsapp}
                                                            >
                                                                <FontAwesomeIcon icon={faWhatsapp} />
                                                            </a>
                                                        </li>
                                                    )}
                                                    {agencyData.facebook && typeof appConfig.socialLinks.facebook === 'string' && (
                                                        <li className="mr-2">
                                                            <a href={appConfig.socialLinks.facebook} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.facebookTitle')}`}>
                                                                <FontAwesomeIcon icon={faFacebookF} />
                                                            </a>
                                                        </li>
                                                    )}
                                                    {agencyData.instagram && typeof appConfig.socialLinks.instagram === 'string' && (
                                                        <li className="mr-2">
                                                            <a href={appConfig.socialLinks.instagram} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.instagramTitle')}`}>
                                                                <FontAwesomeIcon icon={faInstagram} />
                                                            </a>
                                                        </li>
                                                    )}
                                                    {agencyData.youtube && typeof appConfig.socialLinks.youtube === 'string' && (
                                                        <li className="mr-2">
                                                            <a href={appConfig.socialLinks.youtube} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.youtubeTitle')}`}>
                                                                <FontAwesomeIcon icon={faYoutube} />
                                                            </a>
                                                        </li>
                                                    )}
                                                    {agencyData.twitter && typeof appConfig.socialLinks.twitter === 'string' && (
                                                        <li className="mr-2">
                                                            <a href={appConfig.socialLinks.twitter} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.twitterTitle')}`}>
                                                                <FontAwesomeIcon icon={faTwitter} />
                                                            </a>
                                                        </li>
                                                    )}
                                                    {agencyData.linkedin && typeof appConfig.socialLinks.linkedin === 'string' && (
                                                        <li className="mr-2">
                                                            <a href={appConfig.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.linkedinTitle')}`}>
                                                                <FontAwesomeIcon icon={faLinkedin} />
                                                            </a>
                                                        </li>
                                                    )}
                                                    {agencyData.pinterest && typeof appConfig.socialLinks.pinterest === 'string' && (
                                                        <li>
                                                            <a href={appConfig.socialLinks.pinterest} target="_blank" rel="noopener noreferrer" title={`${t('footer.social.pinterestTitle')}`}>
                                                                <FontAwesomeIcon icon={faPinterest} />
                                                            </a>
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                            {/* Language Selector */}
                                            {languageSelector}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="d-none d-md-block" style={{borderTop: '1px solid #000'}}></div>

                            <nav className="navbar navbar-static-top navbar-expand-md">

                                    {/* Menu Collapse */}
                                    <div className={`navbar-collapse collapse ${!collapsed ? "" : "show"}`} id="navbarCollapse">
                                        {menuLoading ? (
                                            <div className="navbar-nav"><span className="nav-link">{t('common.loading')}...</span></div>
                                        ) : (
                                            <ul className="nav navbar-nav">
                                                {menuItems.map((menuItem, index) => (
                                                    <li key={index} className={`nav-item ${menuItem.dropdown ? 'dropdown' : ''}`}>
                                                        {menuItem.url && menuItem.url !== '#' && !menuItem.dropdown ? (
                                                            <NavLink
                                                                to={`${menuItem.url}`}
                                                                end
                                                                title={menuItem.title}
                                                                className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                                                                onClick={() => handleMenuItemClick(menuItem)}
                                                            >
                                                                {menuItem.label}
                                                            </NavLink>
                                                        ) : (
                                                            <a
                                                                href={menuItem.url || '#'}
                                                                title={menuItem.title}
                                                                className={`nav-link ${menuItem.dropdown ? 'dropdown-toggle' : ''}`}
                                                                role={menuItem.dropdown ? 'button' : undefined}
                                                                aria-haspopup={menuItem.dropdown ? true : undefined}
                                                                aria-expanded={menuItem.dropdown ? false : undefined}
                                                                onClick={(e) => { e.preventDefault(); setDropDownCollapsed(prev => !prev) }}
                                                            >
                                                                {menuItem.label}
                                                                {menuItem.dropdown && (
                                                                    <span className="dropdownchevron ml-1">
                                                                        <FontAwesomeIcon icon={faChevronDown} size="xs" />
                                                                    </span>
                                                                )}
                                                            </a>
                                                        )}
                                                        {/* Dropdown Submenu */}
                                                        {menuItem.dropdown && menuItem.items && (
                                                            <ul className={`dropdown-menu ${!dropDownCollapsed ? "" : "show"}`} aria-labelledby={`navbarDropdown${index}`}>
                                                                {menuItem.items.map((item, subIndex) => (
                                                                    <li key={subIndex}>
                                                                        {item.url && !item.url.startsWith('http') ? (
                                                                            <NavLink
                                                                                to={`${item.url}`}
                                                                                title={item.title}
                                                                                className={({ isActive }) => isActive ? "dropdown-item active" : "dropdown-item"}
                                                                                onClick={() => handleMenuItemClick(item)}
                                                                            >
                                                                                {item.label}
                                                                            </NavLink>
                                                                        ) : (
                                                                            <a
                                                                                href={item.url || '#'}
                                                                                title={item.title}
                                                                                className="dropdown-item"
                                                                                target={item.url && item.url.startsWith('http') ? '_blank' : undefined}
                                                                                rel={item.url && item.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                                                                                onClick={() => handleMenuItemClick(item)}
                                                                            >
                                                                                {item.label}
                                                                            </a>
                                                                        )}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    
                            </nav>

                            {/* Bottone Hamburger */}
                            <button
                                type="button"
                                className={`navbar-toggler ${!collapsed ? "" : "collapsed"} d-md-none`}
                                aria-controls="navbarCollapse"
                                aria-expanded="false"
                                aria-label="Toggle navigation"
                                onClick={() => { setDropDownCollapsed(false); setCollapsed(prev => !prev); }}
                            >
                                <FontAwesomeIcon icon={faBars} />
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </header>
    );
};

export default Header;
